import React, { useState, useEffect } from 'react';
import api from '../utils/api';

// Fallback favicon fetcher from platform URL
const getFavicon = (url) => {
  try {
    const domain = new URL(url).origin;
    return `https://www.google.com/s2/favicons?sz=64&domain_url=${domain}`;
  } catch { return null; }
};

function PlatformLogo({ platform }) {
  const [imgSrc, setImgSrc] = useState(
    platform.logo || getFavicon(platform.downloadUrl)
  );
  const [failed, setFailed] = useState(false);

  if (failed || !imgSrc) {
    return (
      <div style={{ width: 56, height: 56, background: 'var(--gradient-primary)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 22, color: '#fff', flexShrink: 0 }}>
        {platform.name[0]}
      </div>
    );
  }

  return (
    <img
      src={imgSrc}
      alt={platform.name}
      onError={() => {
        if (platform.logo) {
          // If admin-uploaded logo fails, try favicon
          setImgSrc(getFavicon(platform.downloadUrl));
        } else {
          setFailed(true);
        }
      }}
      style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'contain', flexShrink: 0, background: 'var(--bg-secondary)', padding: 4 }}
    />
  );
}

export default function Platforms() {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get('/platforms').then(({ data }) => {
      setPlatforms(data.platforms || []);
    }).finally(() => setLoading(false));
  }, []);

  const handleClick = (id) => api.post(`/platforms/${id}/click`).catch(() => {});

  const filtered = platforms.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    if (filter === 'hot') return matchSearch && p.isHottest;
    return matchSearch;
  });

  return (
    <div style={{ paddingTop: 70 }}>
      <div className="page-header">
        <h1>GAMING PLATFORMS</h1>
        <p>Download and play on the best platforms available</p>
      </div>
      <div className="container" style={{ paddingBottom: 80 }}>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="Search platforms..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 280 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            {['all', 'hot'].map(f => (
              <button key={f} onClick={() => setFilter(f)} className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-secondary'}`} style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {f === 'all' ? '🎮 All' : '🔥 Hottest'}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : (
          <div className="grid-3">
            {filtered.map(p => (
              <div key={p._id} className="card fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'relative' }}>
                {p.isHottest && (
                  <div style={{ position: 'absolute', top: -1, right: 16, background: 'var(--gradient-gold)', padding: '4px 14px', borderRadius: '0 0 10px 10px', fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: '0.08em' }}>🔥 HOTTEST</div>
                )}
                {p.label && !p.isHottest && (
                  <div style={{ position: 'absolute', top: -1, right: 16, borderRadius: '0 0 10px 10px', padding: '4px 14px', fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: '0.08em' }} className={`badge badge-${p.label.toLowerCase()}`}>{p.label}</div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: p.isHottest || p.label ? 20 : 0 }}>
                  <PlatformLogo platform={p} />
                  <div>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, marginBottom: 2 }}>{p.name}</h3>
                    {p.description && <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.4 }}>{p.description}</p>}
                  </div>
                </div>
                <a
                  href={p.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  onClick={() => handleClick(p._id)}
                  style={{ textAlign: 'center' }}
                >
                  ↓ Download / Play Now
                </a>
              </div>
            ))}
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎮</div>
            <p style={{ fontSize: 18 }}>No platforms found</p>
          </div>
        )}
      </div>
    </div>
  );
}
